import { useRef } from 'react';
import GlobalLineIcon from 'remixicon-react/GlobalLineIcon';
import ArticleLineIcon from 'remixicon-react/ArticleLineIcon';
import Layout3LineIcon from 'remixicon-react/Layout3LineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';

import Layout from '@/components/Layout/Vanilla';
import HubSpotForm from '@/components/HubSpotForm';
import Container from '@/components/container';
import Slider from '@/components/Slider';
import TestimonialSlider, {
  Testimonial,
} from '@/components/sections/partners/slider';
import Heading from '@/components/Heading';
import { fetchAPI, gql } from '@/lib/graphcms';
import { scrollTo } from '@/lib/scroll';
import Button from '@/components/Button';
import { HubSpotForm as HubSpotFormType } from '@/types';
import Tabs, { Tab, Card } from '@/components/sections/partners/tabs';

type PartnersPage = {
  page: {
    title: string;
    subTitle: string;
  };
  agencies: Card[];
  ecosystemPartners: Card[];
  hubspotForm: HubSpotFormType;
  testimonials: Testimonial[];
};

const stats = [
  {
    value: `50k+`,
    description: `Teams actively using GraphCMS on production`,
  },
  {
    value: `60+`,
    description: `Implementation and agency partners`,
  },
  {
    value: `25+`,
    description: `First-class ecosystem partners`,
  },
];

export default function Partners({
  page,
  ecosystemPartners,
  agencies,
  hubspotForm,
  testimonials,
}: PartnersPage) {
  const refForm = useRef(null);

  const tabs: Tab[] = [
    {
      id: `agency`,
      title: `Agency Partner`,
      subTitle: `Join the global GraphCMS partner network to succeed together in creating modern API-first digital experiences at scale.`,
      usps: [
        {
          icon: <GlobalLineIcon />,
          title: `World-class experiences`,
          description: `We have a ruthless committment to perfecting user experiences with an equally passionate community of partners across the world.`,
        },
        {
          icon: `GRAPHQL`,
          title: `Modern, API-first stacks`,
          description: `As strong believers in API-first technologies and modern, composable architectures, our implementation and ecosystem partners are aligned with our vision.`,
        },
        {
          icon: <ArticleLineIcon />,
          title: `Co-marketing opportunities`,
          description: `We’re big fans of growing together and sharing our successes. Work with us on case studies, blogs, events, webinars, recordings, and more.`,
        },
      ],
      partnerLink: `/implementation-partners`,
      cards: agencies,
    },
    {
      id: `ecosystem`,
      title: `Ecosystem Partner`,
      subTitle: `Join the GraphCMS ecosystem network to work together in helping create better digital experiences at scale.`,
      usps: [
        {
          icon: <BuildingLineIcon />,
          title: `Ambitious Projects`,
          description: `We work closely with our ecosystem partners to recommend best-of-breed solutions to our customers that contribute to their digital experience goals.`,
        },
        {
          icon: <Layout3LineIcon />,
          title: `First-class ecosystem`,
          description: `As strong believers in composable architctures, we work with the best partners in showcasing how leading APIs federate to deliver better projects.`,
        },
        {
          icon: <ArticleLineIcon />,
          title: `Co-marketing opportunities`,
          description: `We’re big fans of growing together and sharing our successes. Work with us on case studies, blogs, events, webinars, recordings, and more.`,
        },
      ],
      partnerLink: `/technology-ecosystem`,
      cards: ecosystemPartners,
    },
  ];

  function scrollToForm() {
    scrollTo(refForm);
  }

  return (
    <Layout seo={page} removeSalesBreakpoint>
      <div className="max-w-4xl px-6 mx-auto text-center">
        <Heading>{page.title}</Heading>
        <p className="text-lg leading-7 mt-6">{page.subTitle}</p>
        <Button colour="pink" className="mt-10" onClick={scrollToForm}>
          Become a Partner
        </Button>
      </div>

      <div className="bg-whisper mt-40">
        <Container>
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-4xl mx-auto rounded bg-whisper transform -translate-y-1/4 lg:-translate-y-1/2 p-6"
            style={{
              boxShadow: `0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)`,
            }}
          >
            {stats.map((stat) => (
              <div className="text-center" key={stat.value}>
                <div className="text-3xl md:text-5xl font-black text-indigo-600 leading-none">
                  {stat.value}
                </div>
                <div className="text-base md:text-lg leading-snug text-coolGray-500 font-medium mt-2">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </Container>

        <div className="mt-8 lg:mt-20">
          <Heading className="text-center mb-14 pb-1 px-6" level={2}>
            What type of partner are you?
          </Heading>

          <Container>
            <Tabs handlePartnerClick={scrollToForm} tabs={tabs} />
          </Container>
        </div>

        <div className="py-24 lg:py-30">
          <Heading className="text-center mb-10" level={2}>
            Hear from our Partners
          </Heading>

          <div className="mx-auto max-w-5xl px-6">
            <Slider
              items={testimonials}
              Component={TestimonialSlider}
              withOverflowHidden={false}
            />
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto pt-30 pb-16 px-6" ref={refForm}>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-snug text-center mb-14">
          Become a Partner
        </h2>
        <HubSpotForm
          form={hubspotForm}
          buttonColour="pink"
          buttonText="Send request"
        />
      </div>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page, agencies, ecosystemPartners, testimonials } =
    (await fetchAPI(
      gql`
        query partnersPageQuery {
          page(where: { slug: "partners" }) {
            title
            subTitle
          }
          agencies(
            where: { slug_in: ["akqa", "ae-studio", "jung-von-matt-tech"] }
          ) {
            title
            slug
            cardImage {
              alt
              url
            }
          }
          ecosystemPartners(
            where: { slug_in: ["vercel", "netlify", "gatsby"] }
          ) {
            title
            slug
            cardImage {
              alt
              url
            }
          }
          testimonials(
            where: {
              id_in: ["cktsn25cgiblq0b12hozmzo1a", "cktsn5q7cigbu0b12pvcqy43j"]
            }
          ) {
            company {
              caseStudy {
                slug
              }
              name
              url
              logo {
                width
                height
                url(
                  transformation: {
                    image: { resize: { width: 150, fit: clip } }
                  }
                )
                alt
              }
            }
            author {
              name
              title
              bio
              photo {
                width
                height
                url
                alt
              }
            }
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  const response = await fetch(
    `https://api.hubapi.com/forms/v2/forms/cf7f19d8-7e9d-4822-b685-7e9662a18cd0/?hapikey=${process.env.HUBSPOT_API_KEY}`,
  );

  const hubspotForm = await response.json();

  return {
    props: {
      preview,
      page,
      agencies,
      ecosystemPartners,
      hubspotForm,
      testimonials,
    },
    revalidate: 3600,
  };
};
